import { FC, useMemo, useState } from 'react'

import {
  ContextMenu,
  ContextMenuOptionColors,
  ContextMenuTriggerOnActions
} from '@inno-ui/context-menu'
import {
  CheckmarkIcon,
  IconSizes,
  MultiplyIcon,
  PhotoLineIcon
} from '@inno-ui/icons'
import { toast } from '@inno-ui/toast'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { useDeleteWallpaperMutation } from 'shared/api/containerApi/requests/deleteWallpaper'
import { useSetActiveWallpaperMutation } from 'shared/api/containerApi/requests/setActiveWallpaper'
import { useWallpaperStore } from 'shared/stores/wallpaperStore'

import { PlayIcon } from '@innoscripta/icons'

import {
  SELECTED_BANNER_ANIMATION,
  SPRING_TRANSITION
} from '../../WallpapersSelector.constants'
import styles from '../../WallpapersSelector.module.scss'
import itemStyles from './WallpaperItem.module.scss'
import { WallpaperItemProps } from './WallpaperItem.types'

export const WallpaperItem: FC<WallpaperItemProps> = ({
  wallpaper,
  isSelected,
  onClick,
  selectedText,
  isCustom = false
}) => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'desktop.wallpaper'
  })
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false)

  const wallpaperName =
    wallpaper.localizedNames?.[i18n.language] ??
    wallpaper.localizedNames?.[i18n.language.split('-')[0]] ??
    wallpaper.name

  const setCurrentWallpaper = useWallpaperStore(
    state => state.setCurrentWallpaper
  )
  const removeCustomWallpaper = useWallpaperStore(
    state => state.removeCustomWallpaper
  )

  const setActiveWallpaperMutation = useSetActiveWallpaperMutation()

  const deleteWallpaperMutation = useDeleteWallpaperMutation({
    onSuccess: () => {
      toast.success({
        title: (t as (key: string) => string)('delete_success.title'),
        subTitle: (t as (key: string) => string)('delete_success.description')
      })

      removeCustomWallpaper(wallpaper.id)

      const state = useWallpaperStore.getState()
      const { currentWallpaper: current, predefinedWallpapers } = state

      if (current.id === wallpaper.id && predefinedWallpapers.length > 0) {
        const firstPredefined = predefinedWallpapers[0]
        setCurrentWallpaper(firstPredefined)
        setActiveWallpaperMutation.mutate({
          wallpaper_type: 'predefined',
          wallpaper_id: firstPredefined.id
        })
      }
    }
  })

  const renderWallpaperContent = () => (
    <div className={styles.wallpaperItemWrapper}>
      <div className={styles.wallpaperPreview}>
        <img
          onClick={onClick}
          className={styles.wallpaperImg}
          src={wallpaper.thumbnail ?? wallpaper.image}
          alt={wallpaperName ?? ''}
        />
        {wallpaper.animation && (
          <span className={itemStyles.animatedBadge}>
            <PlayIcon size={11} fill='currentColor' aria-hidden='true' />
            {t('animated', 'Animated')}
          </span>
        )}
        <AnimatePresence>
          {isSelected && (
            <motion.div
              key={`selected-${wallpaper.id}`}
              variants={SELECTED_BANNER_ANIMATION}
              initial='initial'
              animate='animate'
              exit='exit'
              transition={SPRING_TRANSITION}
              className={styles.selectedWallpaperBanner}
            >
              <CheckmarkIcon size={IconSizes.Size14} color='var(--white)' />
              {selectedText}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {wallpaperName && (
        <span className={itemStyles.wallpaperName} title={wallpaperName}>
          {wallpaperName}
        </span>
      )}
    </div>
  )

  const contextMenuOptions = useMemo(
    () => [
      {
        icon: PhotoLineIcon,
        label: t('set_as_wallpaper'),
        onClick: onClick
      },
      ...(isCustom
        ? [
            {
              icon: MultiplyIcon,
              label: t('remove_wallpaper'),
              color: ContextMenuOptionColors.Red,
              onClick: () => {
                deleteWallpaperMutation.mutate(String(wallpaper.id))
              }
            }
          ]
        : [])
    ],
    [isCustom, t, onClick, deleteWallpaperMutation, wallpaper.id]
  )

  return (
    <ContextMenu
      isOpen={isContextMenuOpen}
      setIsOpen={setIsContextMenuOpen}
      triggerNode={renderWallpaperContent()}
      triggerOnAction={ContextMenuTriggerOnActions.OnContextMenu}
      options={contextMenuOptions}
      showAtClickPoint
      zIndex={100}
      stopEventPropagation
      triggerWrapperClassName={styles.wallpaperContextMenuTrigger}
    />
  )
}
